import React, { useMemo, useCallback, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  faEye,
  faCheck,
  faTimes,
  faShoppingCart,
  faSpinner,
  faTruck,
  faUserPlus,
  faEnvelope,
  faPrint,
  faComment,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import { useMessageAlert } from '../common/MessageAlert';
import { useChannelSettings } from '../../context/ChannelSettingsContext';
import deliveryOrderService from '../../services/deliveryOrderService';
import ordersService from '../../services/ordersService';


const formatDate = (dateString, t) => {
  if (!dateString) return t('ordersModule.common.symbols.emDash');
  return new Date(dateString).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const getStatusBadge = (status, t) => {
  const s = (status || '').toString().toLowerCase();
  const statuses = t('ordersModule.orders.statuses', { returnObjects: true }) || {};
  const getLabel = (key, fallback) => {
    if (!key) return fallback || String(status);
    return statuses[key] || fallback || String(status);
  };
  if (s === 'approved' || s === 'completed' || s === 'delivered' || s === 'invoiced' || s === 'confirmed') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, String(status))}</span>
      </div>
    );
  }
  if (s === 'rejected' || s === 'cancelled' || s === 'failed') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faTimes} className="text-sm" style={{ color: UI_COLORS.STATUS.ERROR }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.ERROR }}>{getLabel(s, String(status))}</span>
      </div>
    );
  }
  if (s === 'delivery_assigned') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faTruck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, 'Delivery Assigned')}</span>
      </div>
    );
  }
  if (s === 'in_transit') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faTruck} className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, 'In Transit')}</span>
      </div>
    );
  }
  if (['loading', 'loading_started', 'truck_loading', 'truck_load_started', 'truck_loading_started', 'loading_in_progress', 'truck_load'].includes(s)) {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faSpinner} spin className="text-sm" style={{ color: UI_COLORS.STATUS.INFO }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.INFO }}>{getLabel(s, statuses.loading || String(status))}</span>
      </div>
    );
  }
  if (s === 'truck_loaded' || s === 'loaded' || s === 'truck_load_completed') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faCheck} className="text-sm" style={{ color: UI_COLORS.STATUS.SUCCESS }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.SUCCESS }}>{getLabel(s, 'Truck Loaded')}</span>
      </div>
    );
  }
  if (s === 'pending') {
    return (
      <div className="flex items-center space-x-2">
        <FontAwesomeIcon icon={faShoppingCart} className="text-sm" style={{ color: UI_COLORS.STATUS.WARNING }} />
        <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>{getLabel(s, 'Pending')}</span>
      </div>
    );
  }
  return (
    <div className="flex items-center space-x-2">
      <FontAwesomeIcon icon={faShoppingCart} className="text-sm" style={{ color: UI_COLORS.STATUS.WARNING }} />
      <span className="text-sm font-medium" style={{ color: UI_COLORS.STATUS.WARNING }}>{getLabel(s, String(status))}</span>
    </div>
  );
};

const ConfirmedOrdersTable = ({
  orders = [],
  loading = false,
  onViewOrder,
  onAssignSupervisor,
  onRefresh,
  viewLoading = false,
}) => {
  const { t } = useTranslation();


  const [showSendConfirmEmail, setShowSendConfirmEmail] = useState(false);
  const [sendEmailLoading, setSendEmailLoading] = useState(false);
  const [sendConfirmEmailOrderId, setSendConfirmEmailSubOrderId] = useState(null);
  const [pendingChannel, setPendingChannel] = useState('email');
  const { showSuccess, showError } = useMessageAlert();
  const { emailEnabled, whatsappEnabled } = useChannelSettings();
  const hoverColor = useMemo(() => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5), []);

  const openSendDialog = useCallback((id, channel) => {
    setPendingChannel(channel);
    setSendConfirmEmailSubOrderId(id);
    setShowSendConfirmEmail(true);
  }, []);

  const handlePrintGenericOrder = useCallback(async (orderOrId) => {
    const id = typeof orderOrId === 'object' ? orderOrId.id : orderOrId;
    if (!id) return;
    try {
      const res = await deliveryOrderService.getDeliverySubOrderById(id);
      if (res && res.success && res.data) {
        import('../../utils/printHelpers').then(({ printDocument }) => {
          printDocument(res.data, 'Order');
        });
      } else {
        showError(t('common.messages.failedToLoadOrderForPrinting'));
      }
    } catch(e) {
      showError(t('common.messages.failedToLoadOrderForPrinting'));
    }
  }, [showError, t]);

  const columns = useMemo(() => [
    {
      id: 'order_number',
      label: "Order No.",
      sortable: true,
      sortAccessor: (o) => (o?.order_number || '').toLowerCase(),
      renderCell: (o) => (
        <div className="text-sm font-medium whitespace-nowrap" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {o.order_number || t('ordersModule.common.symbols.emDash')}
        </div>
      ),
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'sub_order_number',
      label: "Sub-Order No.",
      sortable: true,
      sortAccessor: (o) => (o?.sub_order_number || '').toLowerCase(),
      renderCell: (o) => (
        <div className="text-xs font-mono whitespace-nowrap" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          {o.sub_order_number || t('ordersModule.common.symbols.emDash')}
        </div>
      ),
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'date',
      label: t('ordersModule.orders.table.columns.date'),
      sortable: true,
      sortAccessor: (o) => (o?.created_at ? new Date(o.created_at).getTime() : 0),
      renderCell: (o) => <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{o.created_at ? formatDate(o.created_at, t) : t('ordersModule.common.symbols.emDash')}</span>,
      skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'tally_name',
      label: t('ordersModule.orders.table.columns.tallyName'),
      sortable: true,
      sortAccessor: (o) => (o?.tally_name || '').toLowerCase(),
      renderCell: (o) => (
        <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
          {o.tally_name || o.customer_name || t('ordersModule.common.symbols.emDash')}
        </div>
      ),
      skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'location_name',
      label: "Location",
      sortable: true,
      sortAccessor: (o) => (o?.location_name || '').toLowerCase(),
      renderCell: (o) => (
        <div className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          {o.location_name || t('ordersModule.common.symbols.emDash')}
        </div>
      ),
      skeleton: <div className="w-32 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'total_quantity',
      label: "Quantity",
      sortable: true,
      sortAccessor: (o) => (o?.total_quantity ? parseFloat(o.total_quantity) : 0),
      renderCell: (o) => (
        <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          {o.total_quantity ? `${o.total_quantity} ${o.unit_type?.toUpperCase() || ''}` : t('ordersModule.common.symbols.emDash')}
        </span>
      ),
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'status',
      label: t('ordersModule.orders.table.columns.status'),
      sortable: true,
      align: 'center',
      sortAccessor: (o) => (o?.status || '').toLowerCase(),
      renderCell: (o) => getStatusBadge(o.status, t),
      skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
    },
    {
      id: 'status_actions',
      label: t('ordersModule.orders.table.columns.statusActions'),
      align: 'center',
      sortable: false,
      renderCell: (o) => (
        // One row of buttons: at narrow widths the table scrolls sideways
        // (DataTable is responsive) instead of dropping WhatsApp to a second line.
        <div className="flex flex-nowrap items-center justify-center gap-2">
          <button
            onClick={() => onAssignSupervisor?.(o.id, o)}
            className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
            style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.12) }}
            title="Assign Supervisor"
          >
            <FontAwesomeIcon icon={faUserPlus} className="text-xs" />
            <span className="text-xs font-medium">Assign Supervisor</span>
          </button>

          {emailEnabled && (
            <button
              onClick={() => openSendDialog(o.order_id, 'email')}
              className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
              style={{ color: UI_COLORS.STATUS.INFO, backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1) }}
              title={t('common.actions.sendEmail')}
            >
              <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
              <span className="text-xs font-medium">Email</span>
            </button>
          )}
          {whatsappEnabled && (
            <button
              onClick={() => openSendDialog(o.order_id, 'whatsapp')}
              className="px-3 py-1.5 rounded-lg flex items-center gap-2 transition-all duration-200 hover:scale-105 whitespace-nowrap"
              style={{ color: '#ffffff', backgroundColor: '#25D366' }}
              title={t('common.actions.sendWhatsApp')}
            >
              <FontAwesomeIcon icon={faComment} className="text-xs" />
              <span className="text-xs font-medium">WhatsApp</span>
            </button>
          )}
        </div>
      ),
      skeleton: (
        <div className="flex items-center justify-center space-x-2">
          <div className="w-8 h-8 bg-gray-300 rounded-lg" />
        </div>
      ),
    },
    {
      id: 'actions',
      label: t('ordersModule.orders.table.columns.actions'),
      align: 'center',
      sortable: false,
      renderCell: (o) => {


        return (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onViewOrder?.(o.id)}
              disabled={viewLoading}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50"
              style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
              title={t('ordersModule.orders.table.tooltips.view')}
            >
              <FontAwesomeIcon icon={faEye} className="text-sm" />
            </button>

            <button
              onClick={() => handlePrintGenericOrder(o.id)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{ color: UI_COLORS.TEXT.PRIMARY, backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35) }}
              title={t('common.actions.printOrder')}
            >
              <FontAwesomeIcon icon={faPrint} className="text-sm" />
            </button>
          </div>
        );
      },
      skeleton: (
        <div className="flex items-center justify-center space-x-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="w-8 h-8 bg-gray-300 rounded-lg" />
          ))}
        </div>
      ),
    },
  ], [
    onViewOrder,
    onAssignSupervisor,
    viewLoading,
    handlePrintGenericOrder,
    emailEnabled,
    whatsappEnabled,
    openSendDialog,
    t,
  ]);

  const getRowProps = useCallback(() => ({
    className: 'hover:bg-opacity-50 transition-colors',
    style: { backgroundColor: 'transparent' },
    onMouseEnter: (event) => { event.currentTarget.style.backgroundColor = hoverColor; },
    onMouseLeave: (event) => { event.currentTarget.style.backgroundColor = 'transparent'; },
  }), [hoverColor]);

  const emptyState = useMemo(() => ({
    icon: faShoppingCart,
    title: t('ordersModule.orders.table.empty.title'),
    message: t('ordersModule.orders.table.empty.message'),
  }), [t]);

  const visibleOrders = useMemo(() => {
    if (!Array.isArray(orders)) return [];
    return orders;
  }, [orders]);

  return (
    <>


      <DataTable
        columns={columns}
        data={visibleOrders}
        loading={loading}
        keyField="id"
        enablePagination={false}
        defaultSort={{ field: 'updated_at', direction: 'desc' }}
        getRowProps={getRowProps}
        emptyState={emptyState}
      />



      <ConfirmDialog
        isOpen={showSendConfirmEmail}
        title={`${t('ordersModule.orders.confirm.sendConfirmEmail.title')} via ${pendingChannel === 'whatsapp' ? 'WhatsApp' : 'Email'}`}
        message={t('ordersModule.orders.confirm.sendConfirmEmail.message')}
        confirmLabel={t('ordersModule.orders.confirm.sendConfirmEmail.confirmLabel')}
        loadingLabel={t('ordersModule.orders.confirm.sendConfirmEmail.loading')}
        cancelLabel={t('ordersModule.orders.confirm.sendConfirmEmail.cancel')}
        loading={sendEmailLoading}
        confirmVariant="primary"
        onCancel={() => { setShowSendConfirmEmail(false); setSendConfirmEmailSubOrderId(null); }}
        onConfirm={async () => {
          if (!sendConfirmEmailOrderId) return;
          setSendEmailLoading(true);
          try {
            const result = await ordersService.sendOrderNotification(sendConfirmEmailOrderId, pendingChannel);
            if (result && result.success) {
              showSuccess(pendingChannel === 'whatsapp' ? 'WhatsApp sent successfully' : t('ordersModule.orders.confirm.sendConfirmEmail.success'));
            } else {
              showError(result.error || t('ordersModule.orders.confirm.sendConfirmEmail.error'));
            }
          } catch {
            showError(t('ordersModule.orders.confirm.sendConfirmEmail.error'));
          } finally {
            setSendEmailLoading(false);
            setShowSendConfirmEmail(false);
            setSendConfirmEmailSubOrderId(null);
          }
        }}
      />
    </>
  );
};

export default ConfirmedOrdersTable;
